import React, { type FC, type MouseEventHandler, type ReactNode, type PropsWithChildren } from 'react';

import styled from '@emotion/styled';
import { variant } from '@lucid/theme';
import { AccordionItemButton, AccordionItemState } from 'react-accessible-accordion';

import type { Lucid } from '../../types/types';
import Box from '../box/box';
import Icon from '../icon/icon';

export interface AccordionButtonProps {
  /** Class(es) to apply to element. */
  className?: string;
  /** The children to be rendered with the Accordion.Button. */
  children?: ReactNode;
  /** Renders an arrow for the `Accordion.Item`. */
  hasArrow?: boolean;
  /** Changes the color for the icon. */
  iconColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  /** Changes the onHover color for the icon. */
  iconHoverColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  /** Passes a custom onClick handler to the `Accordion.Button` component. ` */
  onClick?: () => (void | Promise<boolean>) | MouseEventHandler<HTMLButtonElement>;
  /** The available variants for the `Accordion.Button` component. */
  variant?: Lucid.Theme.Component.Accordion.Button.VariantKeys;
}

const StyledAccordionButton = styled(AccordionItemButton)(
  variant({
    scale: 'accordion.button',
  }),
  {
    label: 'button',
  },
);

const AccordionButton: FC<PropsWithChildren<AccordionButtonProps>> = ({
  children,
  className,
  hasArrow,
  iconColor,
  iconHoverColor,
  onClick,
  variant = 'default',
}) => (
  <StyledAccordionButton className={className} variant={variant}>
    <Box
      onClick={() => {
        if (onClick) {
          onClick();
        }
      }}
    >
      {children}
    </Box>
    {hasArrow && (
      <AccordionItemState>
        {state => (
          <Icon
            color={iconColor}
            css={theme => [
              {
                transition: 'transform 0.5s',
                transitionTimingFunction: theme.beziers.slowStartEase,
              },
              state.expanded && {
                transform: 'rotate(90deg) scale(1.6)',
              },
            ]}
            // eslint-disable-next-line @stylistic/jsx-sort-props
            className="accordionButtonHasArrow"
            hoverColor={iconHoverColor}
            type="CarotRight"
          />
        )}
      </AccordionItemState>
    )}
  </StyledAccordionButton>
);

export { AccordionButton };
export default AccordionButton;
